// Rich content for individual service pages. Each entry extends the base
// `services` mock with: tagline, longDescription, features[], process[],
// faqs[], seoTitle, seoDescription, alsoCalled[] (variant keywords).
export const serviceDetails = {
  inventories: {
    tagline: 'Detailed check-in inventory reports built for UK letting agents.',
    longDescription:
      'A check-in inventory is the single most important document in any tenancy. When a dispute reaches adjudication, the deposit scheme will lean almost entirely on the inventory to decide who pays. Our clerks produce reports that hold up under scrutiny — exhaustive room-by-room descriptions, date-stamped photography, meter readings and smoke alarm checks, all in one report delivered to your inbox.',
    seoTitle:
      'Check-in Inventory Reports for Letting Agents | Residential Support',
    seoDescription:
      'Photo-rich, deposit-dispute-ready check-in inventories from £59. Includes utility meter readings, smoke alarm checks and a fully time-stamped, photographic report.',
    alsoCalled: ['property inventory', 'check-in report', 'schedule of condition'],
    features: [
      'Time-stamped, high-resolution photography of every room and contents',
      'Room-by-room narrative description with condition grading',
      'Utility meter readings (gas, electric, water) with photographs',
      'Smoke and CO alarm checks',
      'Reviewed before the report is released',
      'PDF and HTML report delivered to your inbox within working hours',
    ],
    process: [
      { title: 'Order placed', text: 'Raise a job in the dashboard with the address, key location and any access notes.' },
      { title: 'Clerk allocated', text: 'A local clerk is matched to the property and the booking is confirmed in writing.' },
      { title: 'On-site attendance', text: 'Clerk attends the property, photographs every room and tests all alarms.' },
      { title: 'Report produced', text: 'The draft is reviewed before being released to you.' },
      { title: 'Delivered to you', text: 'The PDF report lands in your inbox and your dashboard.' },
    ],
    faqs: [
      {
        q: 'How are reports stored after delivery?',
        a: 'We email the finished report to your branch and upload it to your dashboard. After that, the report is yours to retain — most agencies file it against the tenancy for the life of the let.',
      },
      {
        q: 'Do you cover furnished and unfurnished properties?',
        a: 'Yes — both are covered. Furnished properties simply involve a longer narrative section for fixtures and contents, and our pricing accounts for that.',
      },
      {
        q: 'How is the report delivered?',
        a: 'Each report arrives by email to your branch and in your dashboard. From there it is yours to forward to the tenant or landlord, and yours to retain on file.',
      },
      {
        q: 'Will the report stand up in a deposit dispute?',
        a: 'Our reports are designed specifically for adjudication. Time-stamped photographs, room-by-room condition grading and the clerk\u2019s narrative are the format the deposit schemes prefer.',
      },
    ],
  },

  viewings: {
    tagline: 'Accompanied viewings, run by trained local agents.',
    longDescription:
      'Letting agents lose hours every week shuttling between branch and property to run viewings. Our accompanied viewings service hands that workload to a vetted local viewer who attends the property, runs a friendly, professional viewing and reports back to your branch with any offer signals.',
    seoTitle:
      'Accompanied Property Viewings for UK Letting Agents | Residential Support',
    seoDescription:
      'Trained local viewers run rental viewings on your behalf from £49. DBS-checked, fully insured and available evenings and weekends across the UK.',
    alsoCalled: ['property viewings', 'accompanied viewings', 'lettings showings'],
    features: [
      'Vetted, DBS-checked local viewers in every UK postcode',
      'Available evenings and weekends — when applicants actually view',
      'Friendly, professional viewings run to a consistent standard',
      'Offer signals and applicant intent passed back to your branch',
      'Multiple bookings on the same day at a discounted rate',
      '£2m public liability cover on every clerk',
    ],
    process: [
      { title: 'Order placed', text: 'Send us the listing, the viewing window and the applicant contact details.' },
      { title: 'Viewer dispatched', text: 'A local viewer is dispatched and confirms the booking with you.' },
      { title: 'Viewing run', text: 'A friendly, professional viewing is conducted at the property.' },
      { title: 'Offer signals captured', text: 'Any interest the applicant shows in the property is noted.' },
      { title: 'Branch updated', text: 'Your branch hears back so you can pick up the negotiation with the applicant.' },
    ],
    faqs: [
      {
        q: 'Do you negotiate offers on our behalf?',
        a: 'Our viewers pick up any offer signals from the applicant, but we deliberately leave the negotiation and offer letter with your branch — that relationship is yours.',
      },
      {
        q: 'How are viewers vetted?',
        a: 'Every clerk is DBS-checked, has at least two recent letting references, completes our internal training and carries our £2m public liability cover.',
      },
      {
        q: 'Can you attend at weekends?',
        a: 'Yes, weekends are some of our busiest days. Saturday and Sunday viewings are scheduled at the same rate as weekdays.',
      },
      {
        q: 'What happens if the applicant cancels at the last minute?',
        a: 'No-shows are charged in full. Our viewer still travels to the property, waits and reports back, so the booking fee applies whether or not the applicant turns up.',
      },
    ],
  },

  photography: {
    tagline: 'Wide-angle photography plus a laser-measured floor plan, in one visit.',
    longDescription:
      'Listings live and die on the first three photos and the floor plan. Our photographers shoot wide-angle and HDR-process every image so interiors are bright and the windows are not blown out. While they are on site they also laser-measure the property and produce a clean 2D floor plan. One booking, two deliverables — at a fraction of the cost of buying them separately.',
    seoTitle:
      'Property Photography & Floor Plans for Letting Agents | Residential Support',
    seoDescription:
      'Wide-angle HDR property photography plus a laser-measured 2D floor plan from £69. Ready for Rightmove and Zoopla, all in one visit.',
    alsoCalled: ['property photography', 'lettings photography', 'floor plans', '2D floor plan'],
    features: [
      'Wide-angle photography with HDR processing so interiors are bright and windows are not blown out',
      'One visit covers both the photography and the floor plan',
      'Laser-measured 2D floor plan included in every booking',
      'Resized variants ready for Rightmove, Zoopla and your own site',
    ],
    process: [
      { title: 'Order placed', text: 'Raise a photography + floor plan job with the address and access details.' },
      { title: 'Photographer attends', text: 'A local photographer attends, stages obvious clutter and shoots room-by-room.' },
      { title: 'Measurement', text: 'The same visit captures laser measurements of every room.' },
      { title: 'Editing & drafting', text: 'Images are HDR-processed and the floor plan is drafted before release.' },
      { title: 'Delivered', text: 'A web gallery, downloads and the floor plan land in your dashboard.' },
    ],
    faqs: [
      {
        q: 'Are the photography and floor plan really included for one price?',
        a: 'Yes — that is the whole point. A single visit captures both, which saves time on site and reduces the combined fee compared with two separate bookings.',
      },
      {
        q: 'Do you stage the property?',
        a: 'We will reposition obvious clutter (cables, towels, used mugs) but we do not bring furniture. For full home staging we partner with a specialist.',
      },
      {
        q: 'How are the images and floor plan delivered?',
        a: 'Everything arrives in your dashboard and is emailed to your branch as soon as the photographer\u2019s edits and the floor-plan draft are signed off internally.',
      },
    ],
  },

  checkout: {
    tagline: 'Check-out inventories that get deposits returned cleanly.',
    longDescription:
      'A check-out inventory is the closing chapter of the tenancy. Done well, it lets you return the deposit promptly with no argument. Done badly, it lands the landlord in a deposit-scheme adjudication they will probably lose. Our clerks attend the property after the tenant has vacated, compare every room line-by-line against the original check-in, photograph anything that has changed, and produce a clear schedule of cleaning and damage charges that the deposit scheme will accept.',
    seoTitle:
      'Check-out Inventory Reports for UK Letting Agents | Residential Support',
    seoDescription:
      'End-of-tenancy check-out inventories from £69. Side-by-side comparison with the check-in, photographic evidence, and a clear damage charge schedule the deposit scheme will accept.',
    alsoCalled: ['check-out inventory', 'end of tenancy inventory', 'dilapidation report'],
    features: [
      'Room-by-room comparison against the original check-in report',
      'Wear-and-tear judgements written to deposit-scheme guidance',
      'Time-stamped photographs of any changes or damage',
      'Closing utility meter readings with photographs',
      'Clear schedule of proposed deductions, ready for the deposit scheme',
      'Final cleanliness assessment with itemised costing if a re-clean is needed',
      'PDF report in your dashboard within working hours',
    ],
    process: [
      { title: 'Order placed', text: 'Book the check-out against the original tenancy in the dashboard.' },
      { title: 'Clerk allocated', text: 'The clerk reads the original check-in before attending so the comparison starts cleanly.' },
      { title: 'On-site attendance', text: 'Clerk attends after the tenant has vacated, photographing room-by-room.' },
      { title: 'Report produced', text: 'Differences are flagged, charges are itemised and the report is reviewed before release.' },
      { title: 'Delivered to you', text: 'The PDF arrives in your inbox with a deposit-scheme-ready breakdown.' },
    ],
    faqs: [
      {
        q: 'What is the difference between a check-in and a check-out inventory?',
        a: 'A check-in records the condition at the start of the tenancy. A check-out is the closing comparison — it documents what has changed since, and proposes which items are fair wear and tear and which warrant a deduction from the deposit.',
      },
      {
        q: 'How is "fair wear and tear" decided?',
        a: 'Our clerks follow the same guidance that the deposit schemes (TDS, DPS and MyDeposits) use — taking into account length of tenancy, number of occupants and the type of item involved. Each charge in the report is reasoned, not assumed.',
      },
      {
        q: 'What if the tenant disputes the report?',
        a: 'Because every line is photographed and reasoned against the original check-in, our reports are the format the deposit-scheme adjudicators prefer. We will also support you with any clarifying notes the scheme requests.',
      },
      {
        q: 'Do you need the tenant to be present?',
        a: 'No — the check-out is run after the tenant has handed back the keys. The report is then issued to your branch, which passes it on to the tenant alongside any proposed deductions.',
      },
    ],
  },
};

export const getServiceDetail = (id) => serviceDetails[id] || null;
